import { Box, Paper, useMediaQuery } from "@material-ui/core";
import Highcharts from "highcharts";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import LineChart from "src/components/ScoringChart/Line/LineChart";
import DateGroupButton from "src/components/ScoringChart/tool-utils/DateGroupButton";
import { NoteTypography } from "src/components/Typography/TitleTypography";
import DetailMonitoringWrapper from "src/components/Wrapper/DetailMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import { useUserAnalysisChartSelector, useUserAnalysisSelector } from "src/context/monitoring/userAnalysisContext";

const dateOption = [
  { label: "1D", number: 24, interval: 1 },
  { label: "1W", number: 84, interval: 2 },
  { label: "1M", number: 90, interval: 8 },
  { label: "3M", number: 90, interval: 24 },
];

const getOption = () => {
  return {
    chart: {
      height: 500,
    },
    xAxis: {
      gridLineWidth: 1,
      type: "datetime",
      labels: {
        enabled: true,
        format: "{value:%e.%b}",
      },
    },
    legend: {
      enabled: true,
    },
    tooltip: {
      useHTML: true,
      style: {
        display: "block",
      },
      formatter: function () {
        const points = this.points;
        let data = "";
        for (let config of points) {
          const color = config.color;
          data += `<br /><div class="scoring-chart-tooltip-container">\
          <div class="scoring-chart-tooltip-circle" style="background:${color}"></div>\
          <span>${config.series.name}</span>:&nbsp;<b>${config.y}</b></div>`;
        }
        const id = this.points[0]["point"].id;
        const times = id.split("-");
        let time1 = times[0].length === 0 ? null : Highcharts.dateFormat("%b %e, %Y, %k:%M", Number(times[0]));
        let time2 = Highcharts.dateFormat("%b %e, %Y, %k:%M", Number(times[1]));
        let result = time1 ? `<div>From: ${time1}</div>` : "";
        result += `<div>To: ${time2}</div>`;
        data = `<div class="scoring-chart-tooltip-x">${result}</div>` + data;
        return data;
      },
    },
  };
};

export default function NOURTransactionTypes() {
  const { pool } = useParams();
  const match = useMediaQuery((theme) => theme.breakpoints.only("xs"));
  const { fetchNOURTransactionTypes } = useUserAnalysisSelector((state) => state.fetch);
  const nourTransactionTypes = useUserAnalysisChartSelector(MONITORING.ANALYSIS.nourTransactionTypes, pool);

  useEffect(() => {
    if (nourTransactionTypes?.active === undefined)
      fetchNOURTransactionTypes(pool, { number: 90, interval: 8 }, { active: 2 });
    else fetchNOURTransactionTypes(pool, nourTransactionTypes?.metadata, { active: nourTransactionTypes?.active });
  }, [nourTransactionTypes?.active, pool]);

  const { series, isLoading } = useMemo(() => {
    if (nourTransactionTypes) {
      return {
        series: [
          { name: "Borrow", data: [].concat(nourTransactionTypes.borrow) },
          { name: "Deposit", data: [].concat(nourTransactionTypes.deposit) },
          { name: "Repay", data: [].concat(nourTransactionTypes.repay) },
          { name: "Withdraw", data: [].concat(nourTransactionTypes.withdraw) },
        ],
        isLoading: false,
      };
    }
    return {
      series: [
        { name: "Borrow", data: [] },
        { name: "Deposit", data: [] },
        { name: "Repay", data: [] },
        { name: "Withdraw", data: [] },
      ],
      isLoading: true,
    };
  }, [nourTransactionTypes]);

  return (
    <DetailMonitoringWrapper
      navigation={{
        text: "user analysis",
        name: "number of users regarding transaction types",
        link: `/user-analysis/${pool}`,
      }}
      selectLink="/user-analysis/number-of-users-regarding-transaction-types"
      Component={{
        Description: (
          <NoteTypography>
            The chart shows the daily number of users who deposit, borrow, withdraw, or repay assets in the lending
            pool. This data indicates {"users'"} transaction behavior and the activeness of the lending pool. For
            instance, the sharp increase in the number of users who deposit their assets in a particular period implies
            the high frequency of depositing in the lending pool at that time.
          </NoteTypography>
        ),
      }}
    >
      <Paper>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <DateGroupButton
            fullWidth={match}
            defaultActive={nourTransactionTypes?.active ?? 0}
            picker={{
              continueClick: (from, to, active) => {
                fetchNOURTransactionTypes(pool, { from, to }, { active });
              },
            }}
            config={dateOption.map((item, _) => ({
              data: item,
              onClick: (data, active) => {
                fetchNOURTransactionTypes(pool, { number: data.number, interval: data.interval }, { active });
              },
            }))}
          />
        </Box>
        <LineChart isLoading={isLoading} series={series} option={getOption()} />
      </Paper>
    </DetailMonitoringWrapper>
  );
}
